import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface InvoicingMonthSettings {
  forecastMonths: number;
  closingMonths: number;
  regenerateMonths: number;
}

export interface InvoicingMonthSettingsRow extends InvoicingMonthSettings {
  key: string; // YYYY-MM
  label: string; // e.g. January
}

interface InvoicingSettingsMonthDto {
  year: number;
  month: number; // 1..12
  closingMonths: number;
  forecastMonths: number;
  regenerateMonths: number;
  active?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class InvoicingSettingsService {
  private readonly baseUrl = `${environment.apiUrl}/configuration/invoicing-settings`;

  private readonly defaultForecastMonths = 3;
  private readonly defaultClosingMonths = 1;
  private readonly defaultRegenerateMonths = 6;

  private readonly maxForecastMonths = 12;
  private readonly maxClosingMonths = 12;
  private readonly maxRegenerateMonths = 12;

  private readonly settingsByYear = new Map<number, Map<number, InvoicingMonthSettings>>();
  private readonly yearRequest$ = new Map<number, Observable<InvoicingSettingsMonthDto[]>>();

  constructor(private readonly http: HttpClient) {}

  /**
   * Backwards-compatible name: previously migrated localStorage.
   * Now it triggers backend loading & caches in-memory.
   */
  ensureMigratedForYear(year: number): void {
    this.ensureYearLoaded(year).subscribe({ error: () => {} });
  }

  ensureYearLoaded(year: number): Observable<void> {
    return this.loadYearDtos(year).pipe(map(() => void 0));
  }

  fetchYearRows(year: number, locale = 'en-US'): Observable<InvoicingMonthSettingsRow[]> {
    return this.loadYearDtos(year).pipe(
      map(() => this.getYearRows(year, locale)),
    );
  }

  saveYearRows(
    year: number,
    rows: Array<Pick<InvoicingMonthSettingsRow, 'key' | 'forecastMonths' | 'closingMonths' | 'regenerateMonths'>>
  ): Observable<InvoicingMonthSettingsRow[]> {
    const payload: InvoicingSettingsMonthDto[] = rows.map((r) => {
      const parsed = this.parseKey(r.key);
      return {
        year,
        month: parsed.month,
        forecastMonths: this.clampInt(r.forecastMonths, 1, this.maxForecastMonths, this.defaultForecastMonths),
        closingMonths: this.clampInt(r.closingMonths, 1, this.maxClosingMonths, this.defaultClosingMonths),
        regenerateMonths: this.clampInt(r.regenerateMonths, 1, this.maxRegenerateMonths, this.defaultRegenerateMonths),
        active: true,
      };
    });

    const params = new HttpParams().set('year', String(year));
    return this.http.put<InvoicingSettingsMonthDto[]>(this.baseUrl, payload, { params }).pipe(
      tap((dtos) => this.writeCache(year, dtos)),
      map(() => this.getYearRows(year)),
    );
  }

  /**
   * Hydrate / overwrite a given year's months from backend rows.
   * Keeps the local-storage shape used by Finance screens (sync reads).
   */
  setYearFromBackendRows(
    year: number,
    rows: ReadonlyArray<{
      month: number;
      forecastMonths: number;
      closingMonths: number;
      regenerateMonths: number;
      active?: boolean;
    }>,
  ): void {
    const mapByMonth = new Map<number, InvoicingMonthSettings>();
    for (const row of rows ?? []) {
      const month = Number(row?.month);
      if (!Number.isInteger(month) || month < 1 || month > 12) {
        continue;
      }
      mapByMonth.set(month, this.normalizeSettings({
        forecastMonths: row.forecastMonths,
        closingMonths: row.closingMonths,
        regenerateMonths: row.regenerateMonths,
      }));
    }
    this.settingsByYear.set(year, mapByMonth);
  }

  getSettingsForMonth(date: Date): InvoicingMonthSettings {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const stored = this.settingsByYear.get(year)?.get(month);
    return this.normalizeSettings(stored);
  }

  getYearRows(year: number, locale = 'en-US'): InvoicingMonthSettingsRow[] {
    const rows: InvoicingMonthSettingsRow[] = [];
    const yearMap = this.settingsByYear.get(year);

    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const date = new Date(year, monthIndex, 1);
      const key = this.getMonthKey(year, monthIndex);
      const stored = yearMap?.get(monthIndex + 1);
      const normalized = this.normalizeSettings(stored);
      rows.push({
        key,
        label: date.toLocaleString(locale, { month: 'long' }),
        ...normalized,
      });
    }

    return rows;
  }

  private loadYearDtos(year: number): Observable<InvoicingSettingsMonthDto[]> {
    const existingRequest = this.yearRequest$.get(year);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new HttpParams().set('year', String(year));
    const request$ = this.http.get<InvoicingSettingsMonthDto[]>(this.baseUrl, { params }).pipe(
      tap((dtos) => this.writeCache(year, dtos)),
      shareReplay(1),
      catchError((error) => {
        this.yearRequest$.delete(year);
        return throwError(() => error);
      }),
    );

    this.yearRequest$.set(year, request$);
    return request$;
  }

  private writeCache(year: number, dtos: InvoicingSettingsMonthDto[]): void {
    const mapByMonth = new Map<number, InvoicingMonthSettings>();
    for (const dto of dtos ?? []) {
      if (!dto || dto.year !== year || dto.month < 1 || dto.month > 12) {
        continue;
      }
      mapByMonth.set(dto.month, this.normalizeSettings(dto));
    }
    this.settingsByYear.set(year, mapByMonth);
  }

  private normalizeSettings(input?: Partial<InvoicingMonthSettings> | null): InvoicingMonthSettings {
    const forecastMonths = this.clampInt(
      input?.forecastMonths,
      1,
      this.maxForecastMonths,
      this.defaultForecastMonths
    );
    const closingMonths = this.clampInt(
      input?.closingMonths,
      1,
      this.maxClosingMonths,
      this.defaultClosingMonths
    );
    const regenerateMonths = this.clampInt(
      input?.regenerateMonths,
      1,
      this.maxRegenerateMonths,
      this.defaultRegenerateMonths
    );

    return { forecastMonths, closingMonths, regenerateMonths };
  }

  private clampInt(value: unknown, min: number, max: number, fallback: number): number {
    const numberValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numberValue)) {
      return fallback;
    }
    return Math.min(max, Math.max(min, Math.trunc(numberValue)));
  }

  private parseKey(key: string): { year: number; month: number } {
    const parts = String(key).split('-');
    const year = Number.parseInt(parts[0] ?? '', 10);
    const month = Number.parseInt(parts[1] ?? '', 10);
    return { year, month };
  }

  private getMonthKey(year: number, monthIndex: number): string {
    const paddedMonth = String(monthIndex + 1).padStart(2, '0');
    return `${year}-${paddedMonth}`;
  }
}
